import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { tokenRequest } from '@/config/msalConfig';
import { needsInteraction, INTERACTIVE_ATTEMPT_KEY } from './acquireToken';

/**
 * Resolves the signed-in user's app roles for the gate (SPA-Auth Standard).
 *
 * **Roles come from the API ACCESS token, not just the ID token** (rule 1).
 * App-role assignments for an API resource are authoritatively carried by the
 * access token the API authorises on; the ID token frequently omits `roles`,
 * which would wrongly deny a correctly-assigned user. We silently acquire the
 * API access token, decode its `roles`, and UNION it with the ID-token roles.
 * While acquisition is in flight `rolesStatus` is `'loading'` so the gate
 * shows a spinner — never a no-access screen (rule 5).
 */

export interface TenantContextValue {
  /** UNION of ID-token roles and API access-token roles. */
  roles: string[];
  /** 'loading' while the access token is being acquired for role resolution. */
  rolesStatus: 'loading' | 'ready';
  /** Diagnostics surfaced on the no-access screen (rule 7). */
  diagnostics: {
    idTokenRoles: string[];
    accessTokenRoles: string[];
    acquireError: string | null;
  };
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Decode the `roles` claim from a JWT access token (base64url payload).
 * Read-only client-side decode — the token is NOT validated here (the API
 * validates it); the UI only mirrors the roles the API will authorise on.
 * Returns `[]` on any malformed input.
 */
export function rolesFromJwt(token: string): string[] {
  try {
    const payload = token.split('.')[1];
    if (!payload) return [];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(
      decodeURIComponent(
        atob(b64)
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      ),
    ) as { roles?: unknown };
    return Array.isArray(json.roles) ? (json.roles as string[]) : [];
  } catch {
    return [];
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const scopes = tokenRequest.scopes;
  const scopeKey = scopes.join(' ');

  // `null` = not yet resolved (loading) for an authenticated user.
  const [accessRoles, setAccessRoles] = useState<string[] | null>(null);
  const [acquireError, setAcquireError] = useState<string | null>(null);

  const idTokenRoles = useMemo(() => {
    const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
    return Array.isArray(claims.roles) ? (claims.roles as string[]) : [];
  }, [account]);

  useEffect(() => {
    let cancelled = false;
    if (!account || scopeKey.length === 0) {
      setAccessRoles([]); // nothing to resolve — ID-token roles stand alone
      setAcquireError(null);
      return;
    }
    setAccessRoles(null); // entering loading
    setAcquireError(null);

    // (2) MSAL Browser v3 requires initialize() before any token call —
    // idempotent, so awaiting here guarantees readiness on every path.
    Promise.resolve(instance.initialize())
      .then(() =>
        instance.acquireTokenSilent({
          account,
          scopes,
          // (3) Force a FRESH token from Entra rather than trusting the cache.
          // A cached token can predate the user's app-role assignment, in
          // which case `roles` is empty and an assigned user is wrongly denied.
          forceRefresh: true,
        }),
      )
      .then((res) => {
        if (cancelled) return;
        sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY); // success clears the loop guard
        setAccessRoles(rolesFromJwt(res.accessToken)); // (1) roles from the ACCESS token
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // (4) Silent failed. If interaction is required (expired SSO session,
        // iframe timeout), AUTO-REDIRECT to interactive auth instead of
        // dead-ending — guarded to at most once per session.
        if (needsInteraction(err) && !sessionStorage.getItem(INTERACTIVE_ATTEMPT_KEY)) {
          sessionStorage.setItem(INTERACTIVE_ATTEMPT_KEY, '1');
          void instance.acquireTokenRedirect({ scopes, account });
          return; // navigating away; leave state in 'loading'
        }
        // Genuine error, or interactive already attempted — fall back to
        // ID-token roles and surface the reason for the diagnostics (7).
        setAccessRoles([]);
        const e = err as { errorCode?: string; message?: string };
        setAcquireError(e.errorCode ?? e.message ?? 'token acquisition failed');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopes derives from scopeKey
  }, [account, instance, scopeKey]);

  const rolesStatus: 'loading' | 'ready' =
    account && scopeKey.length > 0 && accessRoles === null ? 'loading' : 'ready';

  const roles = useMemo(() => {
    const set = new Set<string>(idTokenRoles);
    for (const r of accessRoles ?? []) set.add(r);
    return [...set];
  }, [idTokenRoles, accessRoles]);

  const value = useMemo<TenantContextValue>(
    () => ({
      roles,
      rolesStatus,
      diagnostics: {
        idTokenRoles,
        accessTokenRoles: accessRoles ?? [],
        acquireError,
      },
    }),
    [roles, rolesStatus, idTokenRoles, accessRoles, acquireError],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/** Resolved role state for the gate. Throws outside <TenantProvider> — silent defaults would mask a wiring bug. */
export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (value === null) {
    throw new Error('useTenant must be used within a <TenantProvider>.');
  }
  return value;
}
